import { useState, useEffect, useCallback } from "react";
import { Button } from "@shared/ui/button";
import { Plus, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@shared/ui/alert";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@shared/ui/accordion";
import { useConfiguredDocumentTypes } from "./Useconfigureddocumenttypes";
import { useDocumentType } from "@features/cx/consumerManagement/hooks";
import { DocumentCardComponent } from "@features/cx/shared/components/DocumentCardComponent";
import { toast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

export interface Document {
  id: string;
  name: string;
  type: string;
  category: string;
  subCategory: string;
  categoryCode: string;
  subCategoryCode: string;
  size: number;
  progress: number;
  status: "uploading" | "completed" | "error";
  url?: string;
  file?: File;
  needsReupload?: boolean;
  documentType?: string;
  documentSubtype?: string;
}

interface DocumentUploadFormProps {
  initialData?: Document[];
  consumerId?: string | number | null;
  consumerDetails?: any;
  onSave: (data: Document[]) => void;
  onNext?: () => void;
  isEditMode?: boolean;
  showSubmitButton?: boolean;
  onDataChange?: (data: any) => void;
  remoteUtilityId: number;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const validateFile = (file: File): { isValid: boolean; error?: string } => {
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = [
    "application/pdf",
    "image/jpeg",
    "image/jpg",
    "image/png",
  ];

  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File "${file.name}" exceeds the 5MB size limit`,
    };
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return {
      isValid: false,
      error: `File "${file.name}" is not a supported format. Only PDF, JPG, and PNG files are allowed`,
    };
  }

  return { isValid: true };
};

export function DocumentUploadForm({
  initialData = [],
  consumerId,
  consumerDetails,
  onSave,
  onNext,
  isEditMode = false,
  showSubmitButton = true,
  onDataChange,
  remoteUtilityId,
}: DocumentUploadFormProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<{
    [key: string]: string;
  }>({});
  const [selectedSubTypes, setSelectedSubTypes] = useState<{
    [key: string]: string;
  }>({});
  const [dragging, setDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadedSubtypes, setLoadedSubtypes] = useState<any[]>([]);
  // Get configured document types
  const {
    documentTypes: configuredDocumentTypes,
    isLoading: isConfiguredTypesLoading,
    error: configuredTypesError,
  } = useConfiguredDocumentTypes();
  const utilityId = useRemoteUtilityId();
  // Fetch document types for getting codes/subtypes
  const {
    data: documentTypeData,
    isLoading: isDocumentTypeLoading,
    error: documentTypeError,
  } = useDocumentType({
    remote_utility_id: utilityId,
    config_level: "document_type",
  });

  
  // Transform API documents to component format
const transformApiDocuments = useCallback(
  (apiDocuments: any[]): Document[] => {
    console.log("📄 Transforming API documents:", apiDocuments);

    return apiDocuments.map((doc: any, index: number) => {
      const fileName = doc.file?.split("/").pop() || `Document ${index + 1}`;
      
      const transformed = {
        id: doc.id?.toString() || `api_doc_${index}`,
        name: fileName,
        type: doc.documentType || "", // NAME from API (e.g., "Invoice")
        category: doc.documentType || "",
        subCategory: doc.documentSubtype || "", // NAME from API (e.g., "Final Bill")
        categoryCode: "", // Will be set when matching with config
        subCategoryCode: "",
        size: 0,
        progress: 100,
        status: "completed" as const,
        url: doc.file || "",
        file: undefined,
        needsReupload: false,
        documentType: doc.documentType || "",
        documentSubtype: doc.documentSubtype || "",
      };
      
      console.log(`  ➡️ Transformed:`, transformed);
      return transformed;
    });
  },
  []
);

  const normalizeConfigId = (id: string = "") =>
  id.split("#")[0].trim().toUpperCase();

  const normalizeApiType = (type: string = "") =>
  type.trim().toUpperCase();
  // Initialize documents from API or configured types
useEffect(() => {
  console.log("🔄 Initializing documents");
  console.log("Consumer Details:", consumerDetails);
  console.log("Configured Types:", configuredDocumentTypes);

  if (configuredDocumentTypes.length === 0) {
    console.log("⏳ Waiting for configured types...");
    return;
  }

  // Pre-populate selectedDocumentTypes
  const preSelectedTypes: { [key: string]: string } = {};
  configuredDocumentTypes.forEach((configType) => {
    preSelectedTypes[configType.id] = configType.name;
  });
  setSelectedDocumentTypes(preSelectedTypes);

  if (isEditMode && consumerDetails?.document) {
    const apiDocuments = transformApiDocuments(consumerDetails.document);
    console.log("📦 API Documents:", apiDocuments);

    // Map by document type NAME (not code!)
    const uploadedDocsMap = new Map<string, typeof apiDocuments[0]>();
    apiDocuments.forEach((doc) => {
      const docTypeName = doc.documentType?.trim() || "";
      if (docTypeName) {
        uploadedDocsMap.set(docTypeName, doc);
        console.log(`📌 Mapped: "${docTypeName}"`, doc);
      }
    });

    // Merge with configured types
    const mergedDocuments: Document[] = configuredDocumentTypes.map((configType) => {
      console.log(`\n🔍 Processing: "${configType.name}"`);
      
      // Match by NAME
      const existingDoc = uploadedDocsMap.get(configType.name);

      if (existingDoc) {
        console.log(`  ✅ Found existing doc`);
        return {
          ...existingDoc,
          type: configType.id,
          categoryCode: configType.code,
        };
      }

      console.log(`  ➕ Creating placeholder`);
      return {
        id: `placeholder_${configType.id}`,
        name: "",
        type: configType.id,
        category: configType.name,
        subCategory: "",
        categoryCode: configType.code,
        subCategoryCode: "",
        size: 0,
        progress: 0,
        status: "uploading" as const,
        url: "",
        file: undefined,
        needsReupload: false,
        documentType: configType.name,
        documentSubtype: "",
      };
    });

    console.log("🎯 Merged Documents:", mergedDocuments);
    setDocuments(mergedDocuments);

    // Set subtypes - MATCH BY NAME
    const subtypes: { [key: string]: string } = {};
    
    apiDocuments.forEach((doc) => {
      const configType = configuredDocumentTypes.find(
        (ct) => ct.name === doc.documentType // Simple name match!
      );
      
      if (configType && doc.documentSubtype) {
        subtypes[configType.id] = doc.documentSubtype;
        console.log(`✅ Set subtype "${doc.documentSubtype}" for ${configType.name}`);
      }
    });
    
    console.log("📋 Selected subtypes:", subtypes);
    setSelectedSubTypes(subtypes);
  } else {
    // Create mode - placeholders only
    const placeholderDocs: Document[] = configuredDocumentTypes.map((configType) => ({
      id: `placeholder_${configType.id}`,
      name: "",
      type: configType.id,
      category: configType.name,
      subCategory: "",
      categoryCode: configType.code,
      subCategoryCode: "",
      size: 0,
      progress: 0,
      status: "uploading" as const,
      url: "",
      file: undefined,
      needsReupload: false,
      documentType: configType.name,
      documentSubtype: "",
    }));

    console.log("➕ Placeholders:", placeholderDocs);
    setDocuments(placeholderDocs);
  }
}, [isEditMode, consumerDetails, configuredDocumentTypes, transformApiDocuments]);

  // Handle document change
  const handleDocumentChange = useCallback(
    (updatedDocuments: Document[]) => {
      setDocuments(updatedDocuments);
      if (onSave) onSave(updatedDocuments);

      if (onDataChange) {
        const validFiles = updatedDocuments.filter(
          (doc) => (doc.file || doc.url) && !doc.needsReupload
        );
        const fileObjects = validFiles
          .filter((doc) => doc.file)
          .map((doc) => doc.file!);
        const fileMetadata = updatedDocuments.map((doc) => ({
          id: doc.id,
          name: doc.name,
          type: doc.type,
          category: doc.category,
          subCategory: doc.subCategory,
          categoryCode: doc.categoryCode,
          subCategoryCode: doc.subCategoryCode,
          size: doc.size,
          needsReupload: doc.needsReupload || false,
          url: doc.file,
        }));

        onDataChange({
          documents: updatedDocuments,
          file: fileObjects,
          fileMetadata: fileMetadata,
        });
      }
    },
    [onSave, onDataChange]
  );

  // Upload file
  const uploadFile = useCallback(
    (
      file: File,
      docType: string,
      subType: string,
      cardId: string,
      documentTypeObj: any,
      subtypeCode: string
    ) => {
      const validation = validateFile(file);

      if (!validation.isValid) {
        toast({
          title: "Error",
          description: validation.error,
          variant: "destructive",
        });
        return;
      }

      const documentId = `${file.name}-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const newDocument: Document = {
        id: documentId,
        name: file.name,
        type: cardId,
        category: docType,
        subCategory: subType,
        categoryCode: documentTypeObj?.code || "",
        subCategoryCode: subtypeCode,
        size: file.size,
        progress: 100,
        status: "completed",
        file: file,
        needsReupload: false,
      };

      // Replace placeholder or existing document for this type
      const updatedDocuments = documents.map((doc) =>
        doc.type === cardId ? newDocument : doc
      );

      handleDocumentChange(updatedDocuments);

      toast({
        title: "Success",
        description: `${file.name} has been added`,
      });
    },
    [documents, handleDocumentChange]
  );

  // Remove document
  const removeDocument = useCallback(
    (documentId: string) => {
      // Find the document to remove
      const docToRemove = documents.find((doc) => doc.id === documentId);
      if (!docToRemove) return;

      // Replace with placeholder
      const configType = configuredDocumentTypes.find(
        (ct) => ct.id === docToRemove.type
      );
      if (!configType) return;

      const placeholder: Document = {
        id: `placeholder_${configType.id}`,
        name: "",
        type: configType.id,
        category: configType.name,
        subCategory: "",
        categoryCode: configType.code,
        subCategoryCode: "",
        size: 0,
        progress: 0,
        status: "uploading" as const,
        url: "",
        file: undefined,
        needsReupload: false,
        documentType: configType.name,
        documentSubtype: "",
      };

      const updatedDocuments = documents.map((doc) =>
        doc.id === documentId ? placeholder : doc
      );

      handleDocumentChange(updatedDocuments);

      // Clear subtype selection
      setSelectedSubTypes((prev) => {
        const newSubTypes = { ...prev };
        delete newSubTypes[docToRemove.type];
        return newSubTypes;
      });
    },
    [documents, configuredDocumentTypes, handleDocumentChange]
  );

  // Handle subtype change
  const handleSubtypeChange = useCallback((cardId: string, subtype: string) => {
    setSelectedSubTypes((prev) => ({
      ...prev,
      [cardId]: subtype,
    }));
  }, []);

  // Handle file upload
const handleFileUpload = useCallback(
  (file: File, docType: string, subType: string, cardId: string) => {
    const configType = configuredDocumentTypes.find((ct) => ct.id === cardId);
    const documentTypeObj = configType || { code: "" };

    // Find subtype code from loaded subtypes
    const subtypeObj = loadedSubtypes.find(s => s.name === subType);
    const subtypeCode = subtypeObj?.code || "";
    
    console.log("📤 Upload:", { docType, subType, subtypeCode, cardId });
    
    uploadFile(file, docType, subType, cardId, documentTypeObj, subtypeCode);
  },
  [configuredDocumentTypes, loadedSubtypes, uploadFile]
);
  // Check if upload is disabled
  const isUploadDisabled = useCallback(
    (cardId: string) => {
      return !selectedSubTypes[cardId];
    },
    [selectedSubTypes]
  );

  // Check if all required documents are uploaded
const areAllRequiredDocumentsUploaded = () => {
  const requiredTypes = configuredDocumentTypes.filter((doc) => doc.required);
  
  console.log("🔍 Checking required documents:");
  console.log("  Required types:", requiredTypes.map(t => t.name));
  console.log("  Current documents:", documents.map(d => ({
    type: d.type,
    category: d.category,
    hasFile: !!(d.file || d.url),
    needsReupload: d.needsReupload,
    status: d.status
  })));
  
  const allUploaded = requiredTypes.every((requiredType) => {
    const doc = documents.find((d) => d.type === requiredType.id);
    const isUploaded = doc && (doc.file || doc.url) && !doc.needsReupload;
    
    console.log(`  ${requiredType.name}: ${isUploaded ? '✅ UPLOADED' : '❌ MISSING'}`);
    if (doc) {
      console.log(`    - Has file: ${!!doc.file}, Has URL: ${!!doc.url}, Needs reupload: ${doc.needsReupload}`);
    }
    
    return isUploaded;
  });
  
  console.log(`\n📊 Result: All required uploaded = ${allUploaded}`);
  return allUploaded;
};

  // Handle form submission
  const handleSubmit = async () => {
    if (!areAllRequiredDocumentsUploaded()) {
      const missingDocs = configuredDocumentTypes
        .filter((type) => type.required)
        .filter((type) => {
          const doc = documents.find((d) => d.type === type.id);
          return !doc || (!doc.file && !doc.url) || doc.needsReupload;
        })
        .map((type) => type.name);

      toast({
        title: "Missing Required Documents",
        description: `Please upload required documents: ${missingDocs.join(", ")}`,
        variant: "destructive",
      });

      return;
    }

    setIsSubmitting(true);

    try {
      const validDocuments = documents.filter(
        (doc) => (doc.file || doc.url) && !doc.needsReupload
      );

      if (onDataChange) {
        onDataChange({
          documents: documents,
          file: validDocuments.filter((doc) => doc.file).map((doc) => doc.file!),
          fileMetadata: documents.map((doc) => ({
            id: doc.id,
            name: doc.name,
            type: doc.type,
            category: doc.category,
            subCategory: doc.subCategory,
            categoryCode: doc.categoryCode,
            subCategoryCode: doc.subCategoryCode,
            size: doc.size,
            needsReupload: doc.needsReupload || false,
            url: doc.url,
          })),
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        });
      }

      if (onSave) onSave(documents);
      if (onNext) onNext();
    } catch (error) {
      console.error("Error submitting documents:", error);
      toast({
        title: "Error",
        description: "Failed to submit documents",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragging(false);
  }, []);

  // Show errors
  if (configuredTypesError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>Failed to load configured document types</AlertDescription>
      </Alert>
    );
  }

  if (documentTypeError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>Failed to load document types</AlertDescription>
      </Alert>
    );
  }

  const isLoading = isConfiguredTypesLoading || isDocumentTypeLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3">Loading document types...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full">
      <Accordion type="single" collapsible className="mb-6">
        <AccordionItem
          value="document-info"
          className="border border-blue-200 rounded-lg bg-blue-50"
        >
          <AccordionTrigger className="px-4 py-3 hover:bg-blue-100 rounded-t-lg">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-blue-500 mr-3" />
              <span className="font-medium text-blue-800">Document Information</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ul className="text-blue-700 text-sm space-y-1 list-disc pl-5">
              <li>All documents must be clear and legible</li>
              <li>Accepted formats: JPG, PNG, PDF (max 5MB each)</li>
              <li>Upload at least one document for each required type</li>
              {isEditMode && <li>You can replace existing documents or add new ones</li>}
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {configuredDocumentTypes.map((configType, index) => {
          const doc = documents.find((d) => d.type === configType.id);
          if (!doc) return null;

          // Get all documents for this card type (should be just one or placeholder)
          const cardDocuments = documents.filter((d) => d.type === configType.id);

          return (
            <DocumentCardComponent
              key={configType.id}
              doc={{
                id: configType.id,
                name: configType.name,
                code: configType.code,
                required: configType.required,
              }}
              index={index}
              documents={cardDocuments}
              selectedDocumentTypes={selectedDocumentTypes}
              selectedSubTypes={selectedSubTypes}
              documentTypeData={documentTypeData}
              isEditMode={isEditMode}
              isDocumentTypeLoading={isDocumentTypeLoading}
              dragging={dragging}
              onDocumentTypeChange={() => {}}
              onSubtypeChange={handleSubtypeChange}
              onFileUpload={handleFileUpload}
              onRemoveDocument={removeDocument}
              onRemoveCard={() => {}}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              isUploadDisabled={isUploadDisabled}
              canRemoveCard={false}
              hideDocumentTypeDropdown={true}
              
            />
          );
        })}
      </div>

      {showSubmitButton && (
        <div className="flex flex-col items-end gap-2">
          <Button
            onClick={handleSubmit}
            className="w-full md:w-auto"
            disabled={isSubmitting || !areAllRequiredDocumentsUploaded()}
          >
            {isSubmitting
              ? isEditMode
                ? "Updating..."
                : "Uploading..."
              : isEditMode
              ? "Update and Continue"
              : "Save and Continue"}
          </Button>
          {!areAllRequiredDocumentsUploaded() ? (
            <p className="text-sm text-red-600">
              Please upload all required documents to continue
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Optional documents can be uploaded now or skipped
            </p>
          )}
        </div>
      )}
    </div>
  );
}